import { useAtomValue } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import {
  ButtonGroup,
  ButtonGroupSeparator,
} from "~/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { DropdownIcon } from "~/components/ui/icons/dropdown";
import { toast } from "~/components/ui/toast";
import { FileExtension, useStocksExportQuery } from "~/graphql/graphql-new.gen";
import { track } from "~/libs/analytics";
import { activeSaleIdState } from "~/stores/sales";

export function ProductExport() {
  const { t } = useTranslation();

  const activeSaleId = useAtomValue(activeSaleIdState);
  const [value, setValue] = useState("export-csv");

  const menuOptions = [
    {
      label: t("stock.export.export_stocks_as_csv"),
      value: "export-csv",
      onClick: async () => {
        const result = await refetchStocksExport();
        if (result.data) {
          if (result.data.stocksExport.fileUrl) {
            window.open(result.data.stocksExport.fileUrl, "_self");
            toast.success(result.data.stocksExport.fileUrl);
          } else {
            toast.success(t("stock.export.no_stock"));
          }
        }
        track("ExportStock", {
          format: "csv",
        });
      },
    },
    {
      label: t("stock.export.export_stocks_as_xls"),
      value: "export-xls",
      onClick: async () => {
        const result = await refetchStocksExport();
        if (result.data) {
          if (result.data.stocksExport.fileUrl) {
            window.open(result.data.stocksExport.fileUrl, "_self");
            toast.success(result.data.stocksExport.fileUrl);
          } else {
            toast.success(t("stock.export.no_stock"));
          }
        }
        track("ExportStock", {
          format: "xls",
        });
      },
    },
  ];

  const { isFetching: isLoadingStocksExport, refetch: refetchStocksExport } =
    useStocksExportQuery(
      {
        saleId: activeSaleId ?? "",
        input: {
          fileExtension:
            value === "export-csv" ? FileExtension.Csv : FileExtension.Xlsx,
        },
      },
      {
        enabled: false,
      },
    );
  const selectedMenuOption = menuOptions.find(
    (option) => option.value === value,
  );

  return (
    <ButtonGroup>
      <Button
        type="button"
        data-slot="button"
        onClick={selectedMenuOption?.onClick}
        disabled={isLoadingStocksExport}
      >
        {selectedMenuOption?.label}
      </Button>
      <ButtonGroupSeparator />
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isLoadingStocksExport}>
          <Button type="button" data-slot="button" className="p-2.5">
            <DropdownIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-gray-900 text-white">
          {menuOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => setValue(option.value)}
              data-state={
                selectedMenuOption?.value === option.value
                  ? "selected"
                  : undefined
              }
              className="text-white focus:bg-gray-700 data-[state=selected]:font-bold"
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  );
}
